import { ctor } from 'from2';
import { async } from 'regenerator-runtime';
import {
  req,
  imgServer,
  getWebsiteData,
  googlePlaceDataAPI,
} from '../js/config';
import { currencyFormatter } from '../js/base';
import { preloader, withPreloader } from '../js/preloader';

let PageData = {};
let InventoryData = [];
let currentSortOption = '';
let currentPage = 1;
let itemsPerPage = 12;
let totalPages = 1;

const Initialize = async () => {
  // Initialize preloader manager with timeout protection
  preloader.init();
  
  try {
    // Wrap API calls with timeout protection
    await withPreloader(async () => {
      await getPageData();
      await generatePageHTML();
    }, { timeout: 10000 }); // 10 second timeout for inventory (more data)

    await loadJavascriptNow();
    
    // Hide preloader on success
    preloader.hide();
  } catch (err) {
    console.error('Error in Initialize:', err);
    // Preloader is automatically hidden by withPreloader
    
    // Show error message to user if inventory fails to load
    showInventoryErrorMessage();
  }
};

const getPageData = async () => {
  try {
    PageData = await getWebsiteData();

    if (!PageData || !Array.isArray(PageData)) {
      PageData = [];
    }

    let oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 15);

    PageData = PageData.filter((a) => {
      if (!a || !a.attributes) {
        return false;
      }

      if (a.attributes.Status === 'Available') {
        return true;
      } else if (a.attributes.Status === 'Sold') {
        let updatedAt = new Date(a.attributes.updatedAt);
        return updatedAt >= oneWeekAgo;
      } else {
        return false;
      }
    }).sort((a, b) => parseFloat(b.id) - parseFloat(a.id));

    InventoryData = PageData;
  } catch (err) {
    console.error('Error in getPageData:', err);
    PageData = [];
    InventoryData = [];
    throw err;
  }
};

// Sorting function
const sortInventoryData = (data, sortOption) => {
  if (!sortOption) {
    return data;
  }

  let sortedData = [...data];

  try {
    switch (sortOption) {
      case 'newest':
        sortedData = sortedData.sort(
          (a, b) => parseFloat(b.id) - parseFloat(a.id)
        );
        break;
      case 'oldest':
        sortedData = sortedData.sort(
          (a, b) => parseFloat(a.id) - parseFloat(b.id)
        );
        break;
      case 'price-low':
        sortedData = sortedData.sort((a, b) => {
          const priceA = parseFloat(a.attributes.Price) || 0;
          const priceB = parseFloat(b.attributes.Price) || 0;
          return priceA - priceB;
        });
        break;
      case 'price-high':
        sortedData = sortedData.sort((a, b) => {
          const priceA = parseFloat(a.attributes.Price) || 0;
          const priceB = parseFloat(b.attributes.Price) || 0;
          return priceB - priceA;
        });
        break;
      case 'year-new':
        sortedData = sortedData.sort((a, b) => {
          const yearA = parseInt(a.attributes.Year) || 0;
          const yearB = parseInt(b.attributes.Year) || 0;
          return yearB - yearA;
        });
        break;
      case 'year-old':
        sortedData = sortedData.sort((a, b) => {
          const yearA = parseInt(a.attributes.Year) || 0;
          const yearB = parseInt(b.attributes.Year) || 0;
          return yearA - yearB;
        });
        break;
      case 'mileage-low':
        sortedData = sortedData.sort((a, b) => {
          const mileageA = parseInt(a.attributes.Odometer) || 0;
          const mileageB = parseInt(b.attributes.Odometer) || 0;
          return mileageA - mileageB;
        });
        break;
      case 'mileage-high':
        sortedData = sortedData.sort((a, b) => {
          const mileageA = parseInt(a.attributes.Odometer) || 0;
          const mileageB = parseInt(b.attributes.Odometer) || 0;
          return mileageB - mileageA;
        });
        break;
      case 'make-az':
        sortedData = sortedData.sort((a, b) => {
          const makeA = (a.attributes.Make || '').toLowerCase();
          const makeB = (b.attributes.Make || '').toLowerCase();
          return makeA.localeCompare(makeB);
        });
        break;
      case 'make-za':
        sortedData = sortedData.sort((a, b) => {
          const makeA = (a.attributes.Make || '').toLowerCase();
          const makeB = (b.attributes.Make || '').toLowerCase();
          return makeB.localeCompare(makeA);
        });
        break;
      case 'model-az':
        sortedData = sortedData.sort((a, b) => {
          const modelA = (a.attributes.Model || '').toLowerCase();
          const modelB = (b.attributes.Model || '').toLowerCase();
          return modelA.localeCompare(modelB);
        });
        break;
      case 'model-za':
        sortedData = sortedData.sort((a, b) => {
          const modelA = (a.attributes.Model || '').toLowerCase();
          const modelB = (b.attributes.Model || '').toLowerCase();
          return modelB.localeCompare(modelA);
        });
        break;
      default:
        break;
    }

    return sortedData;
  } catch (err) {
    console.error('Error in sortInventoryData:', err);
    return data;
  }
};

const generatePageHTML = async () => {
  try {
    await inventoryBlock();
    await sideBarBlock();

    const copyrightFooter = document.querySelector('#copyrightYearsFooter');
    const copyrightMobile = document.querySelector('#copyrightYearsMobileMenu');

    if (copyrightFooter) {
      copyrightFooter.innerHTML =
        '&copy;' +
        new Date().getFullYear() +
        ' <span> Hansen Truck & Car Sales</span>';
    }

    if (copyrightMobile) {
      copyrightMobile.innerHTML =
        '&copy;' +
        new Date().getFullYear() +
        ' <span> Hansen Truck & Car Sales</span>';
    }

    try {
      let testimonials = await googlePlaceData();
      if (testimonials != null) {
        await updateCurrentDayHours(testimonials.opening_hours);
        await phoneNumber(testimonials.formatted_phone_number);
        await address(testimonials.formatted_address);
      }
    } catch (googleErr) {
      console.warn('Error getting Google Place data:', googleErr);
    }
  } catch (err) {
    console.error('Error in generatePageHTML:', err);
    throw err;
  }
};

// Add missing functions with error handling
const loadJavascriptNow = async () => {
  try {
    // This function might initialize jQuery plugins, sliders, etc.
    // Add any specific initialization code here
  } catch (err) {
    console.error('Error in loadJavascriptNow:', err);
  }
};

const googlePlaceData = async () => {
  try {
    if (typeof googlePlaceDataAPI === 'function') {
      return await googlePlaceDataAPI();
    } else {
      return null;
    }
  } catch (err) {
    console.warn('Error in googlePlaceData:', err);
    return null;
  }
};

// Pagination function with proper styling
const generatePagination = (totalItems) => {
  if (totalItems <= itemsPerPage) {
    // Hide pagination if not needed
    const paginationContainer = document.querySelector('#pagination-container');
    if (paginationContainer) {
      paginationContainer.innerHTML = '';
    }
    return;
  }

  let paginationContainer = document.querySelector('#pagination-container');
  if (!paginationContainer) {
    // Create pagination container if it doesn't exist
    const tabContent = document.querySelector('.tab-content');
    paginationContainer = document.createElement('div');
    paginationContainer.id = 'pagination-container';
    paginationContainer.className = 'all-pagination';
    tabContent.parentNode.insertBefore(
      paginationContainer,
      tabContent.nextSibling
    );
  }

  let paginationHTML = '<ul class="pagination justify-content-center">';

  // Previous button
  if (currentPage > 1) {
    paginationHTML += `<li>
                        <a href="#" class="previous" onclick="changePage(${
                          currentPage - 1
                        }); return false;">
                          <i class="ion-chevron-left"></i> <span>Previous</span>
                        </a>
                      </li>`;
  }

  // Page numbers
  let startPage = Math.max(1, currentPage - 2);
  let endPage = Math.min(totalPages, currentPage + 2);

  if (startPage > 1) {
    paginationHTML += `<li>
                        <a href="#" onclick="changePage(1); return false;">1</a>
                      </li>`;
    if (startPage > 2) {
      paginationHTML += `<li>
                          <a href="#" class="disabled">...</a>
                        </li>`;
    }
  }

  for (let i = startPage; i <= endPage; i++) {
    const activeClass = i === currentPage ? ' active' : '';
    paginationHTML += `<li>
                        <a href="#" class="${activeClass}" onclick="changePage(${i}); return false;">${i}</a>
                      </li>`;
  }

  if (endPage < totalPages) {
    if (endPage < totalPages - 1) {
      paginationHTML += `<li>
                          <a href="#" class="disabled">...</a>
                        </li>`;
    }
    paginationHTML += `<li>
                        <a href="#" onclick="changePage(${totalPages}); return false;">${totalPages}</a>
                      </li>`;
  }

  // Next button
  if (currentPage < totalPages) {
    paginationHTML += `<li>
                        <a href="#" class="next" onclick="changePage(${
                          currentPage + 1
                        }); return false;">
                          <span>Next</span> <i class="ion-chevron-right"></i>
                        </a>
                      </li>`;
  }

  paginationHTML += '</ul>';
  paginationContainer.innerHTML = paginationHTML;

  console.log(
    `Pagination generated: Page ${currentPage} of ${totalPages} (${totalItems} total items)`
  );
};

// Page change function
const changePage = (page) => {
  if (page >= 1 && page <= totalPages) {
    currentPage = page;
    inventoryBlock();
    // Scroll to top of inventory
    document
      .querySelector('.inventory-area')
      .scrollIntoView({ behavior: 'smooth' });
  }
};

// Make changePage available globally
if (typeof window !== 'undefined') {
  window.changePage = changePage;
}

const updateCurrentDayHours = async (h) => {
  let currentDay = new Date().getDay();
  currentDay == 0 ? (currentDay = 7) : currentDay;

  const currentDayElement = document.querySelector('#currentDayHours');
  if (currentDayElement) {
    currentDayElement.innerHTML = h.weekday_text[currentDay - 1];
  }

  let tpa = '';
  for (let i = 0; i < h.weekday_text.length; i++) {
    tpa += `<li>${h.weekday_text[i]}</li>`;
  }
  const workingHoursElement = document.querySelector('#workingHours');
  if (workingHoursElement) {
    workingHoursElement.innerHTML = tpa;
  }
};

const phoneNumber = async (p) => {
  document.querySelector('#phoneNumber').innerHTML =
    '<a href="tel:+' + p.replace(/[\s\-\(\)]+/g, '') + ' "> ' + p + '</a>';
  document.querySelector('#footerPhoneNumber').innerHTML =
    '<a href="tel:+' + p.replace(/[\s\-\(\)]+/g, '') + ' "> ' + p + '</a>';
};

const address = async (a) => {
  document.querySelector('#address').innerHTML =
    '<a href="https://www.google.com/maps?q=' +
    a +
    '" target="_blank">' +
    a +
    '</a>';
  document.querySelector('#footerAddress').innerHTML =
    '<a href="https://www.google.com/maps?q=' +
    a +
    '" target="_blank">' +
    a +
    '</a>';
};

const sideBarBlock = async () => {
  try {
    // Check if PageData exists and has items
    if (!PageData || !Array.isArray(PageData) || PageData.length === 0) {
      console.warn('No PageData available for sidebar');
      return;
    }

    // Populate filter dropdowns
    try {
      PageData.map((s) => s.attributes.Make)
        .filter((value, index, self) => self.indexOf(value) === index)
        .forEach((element) => {
          $('#allMakesList').append(new Option(element, element));
        });
    } catch (err) {
      console.error('Error populating Make dropdown:', err);
    }

    try {
      PageData.map((s) => s.attributes.Model)
        .filter((value, index, self) => self.indexOf(value) === index)
        .forEach((element) => {
          $('#allModelsList').append(new Option(element, element));
        });
    } catch (err) {
      console.error('Error populating Model dropdown:', err);
    }

    try {
      PageData.map((s) => s.attributes.Year)
        .filter((value, index, self) => self.indexOf(value) === index)
        .forEach((element) => {
          $('#allYearsList').append(new Option(element, element));
        });
    } catch (err) {
      console.error('Error populating Year dropdown:', err);
    }

    try {
      PageData.map((s) => s.attributes.Category)
        .filter((value, index, self) => self.indexOf(value) === index)
        .forEach((element) => {
          $('#allBodyTypes').append(new Option(element, element));
        });
    } catch (err) {
      console.error('Error populating Category dropdown:', err);
    }

    try {
      PageData.map((s) => s.attributes.Transmission)
        .filter((value, index, self) => self.indexOf(value) === index)
        .forEach((element) => {
          $('#allTransmissionList').append(new Option(element, element));
        });
    } catch (err) {
      console.error('Error populating Transmission dropdown:', err);
    }

    try {
      PageData.map((s) => s.attributes.Driveline)
        .filter((value, index, self) => self.indexOf(value) === index)
        .forEach((element) => {
          $('#allDriveList').append(new Option(element, element));
        });
    } catch (err) {
      console.error('Error populating Driveline dropdown:', err);
    }

    try {
      PageData.map((s) => s.attributes.ExteriorColor)
        .filter((value, index, self) => self.indexOf(value) === index)
        .forEach((element) => {
          $('#allExtColorList').append(new Option(element, element));
        });
    } catch (err) {
      console.error('Error populating ExteriorColor dropdown:', err);
    }

    // Initialize SumoSelect AFTER populating options
    try {
      if (typeof $('.optgroup_test').SumoSelect === 'function') {
        $('.optgroup_test').SumoSelect();
      }
    } catch (err) {
      console.error('Error initializing SumoSelect:', err);
    }

    // Bind events
    await bindEventsToSelectLists();
  } catch (err) {
    console.error('Error in sideBarBlock:', err);
    throw err;
  }
};

const bindEventsToSelectLists = async () => {
  try {
    // Helper function to reset page when filtering
    const resetPage = () => {
      currentPage = 1;
    };

    // Sort dropdown event listener
    const sortSelect = $('#sortBySelect');
    if (sortSelect.length) {
      sortSelect.on('sumo:closed change', function () {
        currentSortOption = $(this).val();
        resetPage();
        inventoryBlock();
      });
    }

    // Filter events - bind to both change and sumo:closed for immediate response
    $('#allExtColorList').on('sumo:closed change', function () {
      InventoryData = [];
      let val = $('#allExtColorList option:selected').each(function (i) {
        PageData.filter(
          (s) => s.attributes.ExteriorColor == $(this).val()
        ).forEach(function (e) {
          InventoryData.push(e);
        });
      });
      resetPage();
      inventoryBlock();
    });

    $('#allDriveList').on('sumo:closed change', function () {
      InventoryData = [];
      let val = $('#allDriveList option:selected').each(function (i) {
        PageData.filter((s) => s.attributes.Driveline == $(this).val()).forEach(
          function (e) {
            InventoryData.push(e);
          }
        );
      });
      resetPage();
      inventoryBlock();
    });

    $('#allTransmissionList').on('sumo:closed change', function () {
      InventoryData = [];
      let val = $('#allTransmissionList option:selected').each(function (i) {
        PageData.filter(
          (s) => s.attributes.Transmission == $(this).val()
        ).forEach(function (e) {
          InventoryData.push(e);
        });
      });
      resetPage();
      inventoryBlock();
    });

    $('#allBodyTypes').on('sumo:closed change', function () {
      InventoryData = [];
      let val = $('#allBodyTypes option:selected').each(function (i) {
        PageData.filter((s) => s.attributes.Category == $(this).val()).forEach(
          function (e) {
            InventoryData.push(e);
          }
        );
      });
      resetPage();
      inventoryBlock();
    });

    $('#allYearsList').on('sumo:closed change', function () {
      InventoryData = [];
      let val = $('#allYearsList option:selected').each(function (i) {
        PageData.filter((s) => s.attributes.Year == $(this).val()).forEach(
          function (e) {
            InventoryData.push(e);
          }
        );
      });
      resetPage();
      inventoryBlock();
    });

    $('#allModelsList').on('sumo:closed change', function () {
      InventoryData = [];
      let val = $('#allModelsList option:selected').each(function (i) {
        PageData.filter((s) => s.attributes.Model == $(this).val()).forEach(
          function (e) {
            InventoryData.push(e);
          }
        );
      });
      resetPage();
      inventoryBlock();
    });

    $('#allMakesList').on('sumo:closed change', function () {
      InventoryData = [];
      let val = $('#allMakesList option:selected').each(function (i) {
        PageData.filter((s) => s.attributes.Make == $(this).val()).forEach(
          function (e) {
            InventoryData.push(e);
          }
        );
      });
      resetPage();
      inventoryBlock();
    });

    $('#allMileageList').on('change', function () {
      InventoryData = [];
      let val = $('#allMileageList option:selected').each(function (i) {
        PageData.filter(
          (s) => s.attributes.Odometer <= parseInt($(this).val())
        ).forEach(function (e) {
          InventoryData.push(e);
        });
      });
      resetPage();
      inventoryBlock();
    });

    $('#allPricesList').on('change', function () {
      InventoryData = [];
      let val = $('#allPricesList option:selected').each(function (i) {
        PageData.filter(
          (s) => s.attributes.Price <= parseInt($(this).val())
        ).forEach(function (e) {
          InventoryData.push(e);
        });
      });
      resetPage();
      inventoryBlock();
    });

    // Reset button
    const resetBtn = document.getElementById('BtnResetAll');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        $('#sortBySelect').val('').trigger('change');
        currentSortOption = '';
        currentPage = 1;
        window.location.href = window.location.href;
      });
    }
  } catch (err) {
    console.error('Error binding events:', err);
    throw err;
  }
};

const generateInlineSlider = (x, d) => {
  try {
    if (d && d.attributes && d.attributes.formats != null) {
      let tpl = `<div class="swiper-slide">
                <a href="cardetail.html?id=${
                  x.id
                }&location=brampton&used-car-for-sale=${
        x.attributes.Year + ' ' + x.attributes.Make + ' ' + x.attributes.Model
      }">
                  <img src="${
                    imgServer + d.attributes.formats.small.url
                  }" alt="">
                </a> 
              </div>`;
      return tpl;
    }
    return '';
  } catch (err) {
    console.error('Error in generateInlineSlider:', err);
    return '';
  }
};

const generateInlineSliders = (x) => {
  try {
    let tpl = `<div class="swiper mySwiper">
                <div class="swiper-wrapper">`;

    if (
      x.attributes &&
      x.attributes.Images &&
      x.attributes.Images.data &&
      x.attributes.Images.data.length > 0
    ) {
      tpl += generateInlineSlider(x, x.attributes.Images.data[0]);
    } else {
      // Return a placeholder image slider
      tpl += `<div class="swiper-slide">
               <a href="cardetail.html?id=${
                 x.id
               }&location=brampton&used-car-for-sale=${
        x.attributes.Year + ' ' + x.attributes.Make + ' ' + x.attributes.Model
      }">
                 <img src="assets/images/no-image-placeholder.jpg" alt="No image available">
               </a> 
             </div>`;
    }

    tpl += `</div>
            </div>`;

    return tpl;
  } catch (err) {
    console.error('Error in generateInlineSliders:', err);
    return `<div class="swiper mySwiper">
             <div class="swiper-wrapper">
               <div class="swiper-slide">
                 <img src="assets/images/no-image-placeholder.jpg" alt="Error loading image">
               </div>
             </div>
           </div>`;
  }
};

// Enhanced inventoryBlock function with proper styling
const inventoryBlock = async () => {
  try {
    // If no filters are applied, use all PageData
    if (InventoryData.length == 0) {
      InventoryData = PageData;
    }

    // Apply sorting to the current inventory data
    let sortedData = sortInventoryData(InventoryData, currentSortOption);

    // Update vehicle count
    const vehicleCountElement = document.querySelector('#vehicleCountMatching');
    if (vehicleCountElement) {
      vehicleCountElement.innerHTML =
        sortedData.length + ' Vehicles <span>matching</span>';
    }

    // Clear existing content
    const carGridBlock = document.querySelector('#carGridBlock');
    const listBlock = document.querySelector('#list');

    if (carGridBlock) {
      carGridBlock.innerHTML = '';
    }

    if (listBlock) {
      listBlock.innerHTML = '';
    }

    let cgtpl = '';
    let cltpl = '';

    // Generate car templates
    sortedData.forEach((x, index) => {
      try {
        if (!x || !x.attributes) {
          return;
        }

        // Generate grid template (matching exact CSS structure)
        cgtpl += `<div class="col-xl-4 col-md-6">
                    <div class="single-car-item-2 mt-50">
                      <div class="car-image">
                        ${generateInlineSliders(x)}
                        ${
                          x.attributes.Status === 'Sold'
                            ? '<div class="sale-badge">SOLD</div>'
                            : ''
                        }
                      </div>
                      <div class="car-content">
                        <div class="price">
                          <span class="price-amount">${currencyFormatter.format(
                            x.attributes.Price
                          )}</span>
                        </div>
                        <div class="body-type">
                          <a href="#">${x.attributes.Category || 'Sedan'}</a>
                        </div>
                        <h4 class="car-title">
                          <a href="cardetail.html?id=${
                            x.id
                          }&location=brampton&used-car-for-sale=${
          x.attributes.Year + ' ' + x.attributes.Make + ' ' + x.attributes.Model
        }">${x.attributes.Year} ${x.attributes.Make} ${x.attributes.Model}</a>
                        </h4>
                        <div class="author-meta">
                          <span>${
                            x.attributes.Driveline || 'Front Wheel Drive'
                          }</span>
                        </div>
                        <div class="car-meta">
                          <ul>
                            <li>
                              <a href="#"><i class="ion-gear-a"></i> ${
                                x.attributes.Transmission || 'Automatic'
                              }</a>
                            </li>
                            <li>
                              <a href="#"><i class="ion-ios-speedometer-outline"></i> ${
                                x.attributes.Odometer
                              } km</a>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>`;

        // Generate list template (matching exact CSS structure with description)
        cltpl += `<div class="single-car-item-list mt-50">
                    <div class="car-image">
                      ${generateInlineSliders(x)}
                      ${
                        x.attributes.Status === 'Sold'
                          ? '<div class="sale-badge">SOLD</div>'
                          : ''
                      }
                    </div>
                    <div class="car-content">
                      <div class="price">
                        <span class="price-amount">${currencyFormatter.format(
                          x.attributes.Price
                        )}</span>
                      </div>
                      <div class="content-title">
                        <div class="body-type">
                          <a href="#">${x.attributes.Category || 'Sedan'}</a>
                        </div>
                        <h4 class="car-title">
                          <a href="cardetail.html?id=${
                            x.id
                          }&location=brampton&used-car-for-sale=${
          x.attributes.Year + ' ' + x.attributes.Make + ' ' + x.attributes.Model
        }">${x.attributes.Year} ${x.attributes.Make} ${x.attributes.Model}</a>
                        </h4>
                      </div>
                      <div class="author-meta">
                        <span>${
                          x.attributes.Driveline || 'Front Wheel Drive'
                        }</span>
                      </div>
                      <div class="car-meta">
                        <ul>
                          <li>
                            <div class="glance">
                              <i class="ion-gear-a"></i>
                              <span class="label">TRANSMISSION</span>
                              <span class="value">${
                                x.attributes.Transmission || 'Automatic'
                              }</span>
                            </div>
                          </li>
                          <li>
                            <div class="glance">
                              <i class="ion-android-settings"></i>
                              <span class="label">DRIVE</span>
                              <span class="value">${
                                x.attributes.Driveline || 'Front Wheel Drive'
                              }</span>
                            </div>
                          </li>
                          <li>
                            <div class="glance">
                              <i class="ion-ios-speedometer-outline"></i>
                              <span class="label">MILEAGE</span>
                              <span class="value">${
                                x.attributes.Odometer
                              } km</span>
                            </div>
                          </li>
                        </ul>
                      </div>
                      <div class="car-description">
                        <p>${
                          x.attributes.Description ||
                          'THE ' +
                            x.attributes.Year +
                            ' ' +
                            x.attributes.Make.toUpperCase() +
                            ' ' +
                            x.attributes.Model.toUpperCase() +
                            ' ' +
                            (x.attributes.Trim || 'TRIM').toUpperCase() +
                            ' HAS ' +
                            (x.attributes.Features
                              ? x.attributes.Features.split(',')
                                  .slice(0, 3)
                                  .join(', ')
                                  .toUpperCase()
                              : 'HEATED AND LEATHER SEATS, SUNROOF, NO RUST, GOOD IN CONDITION') +
                            ', COMES CERTIFIED.'
                        }</p>
                      </div>
                    </div>
                  </div>`;
      } catch (itemErr) {
        console.error('Error generating template for item', index, itemErr);
      }
    });

    // Set the generated HTML
    if (carGridBlock) {
      carGridBlock.innerHTML = cgtpl;
    }
    if (listBlock) {
      listBlock.innerHTML = cltpl;
    }
  } catch (err) {
    console.error('Error in inventoryBlock:', err);
    throw err;
  }
};

// Initialize when DOM is ready
console.log('🟢 Script loaded, waiting for DOM...');

document.addEventListener('DOMContentLoaded', function () {
  console.log('🟢 DOM loaded, initializing...');
  Initialize().catch((err) => {
    console.error('❌ Fatal error during initialization:', err);
  });
});

// Also try to initialize immediately in case DOM is already ready
if (document.readyState === 'loading') {
  console.log('🟡 DOM still loading, waiting...');
} else {
  console.log('🟢 DOM already ready, initializing immediately...');
  Initialize().catch((err) => {
    console.error('❌ Fatal error during immediate initialization:', err);
  });
}

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { Initialize };
} else if (typeof window !== 'undefined') {
  window.InventoryInitialize = Initialize;
}

console.log('🟢 Script setup complete');

// Function to show error message when inventory fails to load
const showInventoryErrorMessage = () => {
  const inventoryContainer = document.querySelector('.inventory-items');
  if (inventoryContainer) {
    inventoryContainer.innerHTML = `
      <div class="col-12 text-center py-5">
        <div class="alert alert-warning" role="alert">
          <h4 class="alert-heading">Unable to Load Inventory</h4>
          <p>We're experiencing technical difficulties loading our vehicle inventory.</p>
          <hr>
          <p class="mb-0">Please try refreshing the page or contact us at <a href="tel:9054630928">(905) 463-0928</a> for immediate assistance.</p>
          <button class="btn btn-primary mt-3" onclick="window.location.reload()">Refresh Page</button>
        </div>
      </div>
    `;
  }
};
